import { useRef, useState } from 'react';

import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddAPhotoOutlinedIcon from '@material-ui/icons/AddAPhotoOutlined';
import { useSnackbar } from 'notistack';
// Image Crop
import 'react-image-crop/dist/ReactCrop.css';
import Upload, { UploadProps } from 'rc-upload';
import { RcFile } from 'rc-upload/lib/interface';
import ReactCrop, { Crop } from 'react-image-crop';
// RC Upload

//
import DialogHeader from 'components/DialogHeader';
import ImageViewer from 'components/ImageViewer';
import { useThemeContext } from 'contexts/ThemeContext';
import useAuth from 'hooks/useAuth';
import useCurrentUser from 'hooks/useCurrentUser';
import { useAppDispatch } from 'store';
import { atualizarFotoPerfil, UsuarioInterface } from 'store/reducers/usuarios';
import getBase64 from 'utils/getBase64';

const defaultCrop = {
  unit: '%',
  width: 100,
  aspect: 4 / 4,
} as Crop;

const useStyles = makeStyles(({ palette, shadows, shape, transitions }) => ({
  avatar: {
    margin: '0 auto',
    flexShrink: 0,
  },
  iconButton: {
    background: `${palette.background.paper} !important`,
    position: 'absolute',
    bottom: 0,
    right: 0,
    '&:hover, &:focus': {
      boxShadow: shadows[3],
      color: palette.primary.main,
      transition: transitions.create(['color', 'background', 'box-shadow']),
    },
  },
  reactCrop: {
    width: '100%',
    height: '100%',
    borderRadius: shape.borderRadius + 10,
  },
}));

export default function UploadAvatarProfile() {
  const classes = useStyles();
  const auth = useAuth();
  const {
    state: { fontSize },
  } = useThemeContext();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useCurrentUser();
  const imgRef = useRef<HTMLImageElement>();
  const [imgBase64, setImgBase64] = useState('');
  const [fileUploaded, setFileUploaded] = useState<RcFile | undefined>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [crop, setCrop] = useState<Crop>(defaultCrop);
  const [loadingUpload, setLoadingUpload] = useState(false);

  const uploadProps: UploadProps = {
    name: 'avatar',
    accept: 'image/*',
    multiple: false,
    autoFocus: false,
    disabled: loadingUpload,
    customRequest({ file, onSuccess }) {
      setTimeout(() => {
        const xhr = new XMLHttpRequest();
        if (onSuccess) onSuccess(file, xhr);
      }, 0);
    },
    beforeUpload(file) {
      const isLt2M = file.size / 1024 / 1024 < 2; // 2 MB
      const isJpgOrPng =
        file.type === 'image/jpeg' || file.type === 'image/png';

      return new Promise((resolve, reject) => {
        // Apenas arquivos no formato JPG ou PNG
        if (!isJpgOrPng) {
          enqueueSnackbar('Envie apenas arquivos no formato JPG ou PNG.');
          return reject();
        }
        // Apenas arquivos de 2MB de tamanho máximo
        if (!isLt2M) {
          enqueueSnackbar('O arquivo deve ter no máximo 2 MB.');
          return reject();
        }
        return resolve(file);
      });
    },
    onSuccess(ret, file) {
      getBase64(file, base64 => {
        setFileUploaded(file);
        setImgBase64(base64);
        setDialogOpen(true);
      });
    },
    onError() {
      enqueueSnackbar('Não conseguimos fazer o upload da imagem.');
    },
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const onExited = () => {
    setImgBase64('');
    setCrop(defaultCrop);
    setFileUploaded(undefined);
  };

  const onImageLoaded = (target: HTMLImageElement) => {
    imgRef.current = target;
  };

  const handleUpload = async () => {
    setDialogOpen(false);
    setLoadingUpload(true);

    const imageRef = imgRef.current;
    const file = fileUploaded;

    if (imageRef && file) {
      handleClose(); // Fecha o dialog de recorte

      const scaleX = imageRef.naturalWidth / imageRef.width;
      const scaleY = imageRef.naturalHeight / imageRef.height;
      const data = {
        file,
        scaleX,
        scaleY,
        x: crop.x,
        y: crop.y,
        width: crop.width,
        height: crop.height,
        aspect: crop.aspect,
      };

      dispatch(atualizarFotoPerfil(data))
        .unwrap()
        .then(response => {
          const { foto } = response;
          // Atualizar foto do usuario logado na sessao
          if (foto) {
            const user = { ...currentUser, foto } as UsuarioInterface;
            auth.setCurrentUser(user);
          }

          setLoadingUpload(false);
          enqueueSnackbar('Sua foto do perfil foi atualizada.');
        })
        .catch(error => {
          setLoadingUpload(false);
          if (error.response) enqueueSnackbar(error.response.data.message);
        });
    }
  };

  return (
    <>
      <Box mb={1} display="inline-block" position="relative">
        <ImageViewer src={currentUser?.foto}>
          <Avatar
            src={currentUser?.foto}
            className={classes.avatar}
            style={{
              width: Math.round(fontSize * 4),
              height: Math.round(fontSize * 4),
            }}
          />
        </ImageViewer>

        <Upload {...uploadProps}>
          <Tooltip arrow placement="top" title="Atualizar foto">
            <IconButton
              disabled={loadingUpload}
              className={classes.iconButton}
              style={{
                width: Math.round(fontSize * 1.8),
                height: Math.round(fontSize * 1.8),
                bottom: 0,
              }}
            >
              {loadingUpload ? (
                <CircularProgress size={Math.round(fontSize * 1.33)} />
              ) : (
                <AddAPhotoOutlinedIcon
                  style={{ fontSize: Math.round(fontSize * 1.33) }}
                />
              )}
            </IconButton>
          </Tooltip>
        </Upload>
      </Box>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={dialogOpen}
        onClose={handleClose}
        TransitionProps={{ onExited }}
      >
        <DialogHeader title="EDITAR FOTO" onClose={handleClose} />
        <DialogContent>
          <ReactCrop
            src={imgBase64}
            crop={crop}
            circularCrop
            minWidth={100}
            minHeight={100}
            onChange={setCrop}
            onImageLoaded={onImageLoaded}
            className={classes.reactCrop}
            imageStyle={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={handleUpload}>
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
